import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'
import { formatTime } from '../lib/dates.js'

/**
 * A time of day, entered the way macOS enters one.
 *
 * What this replaced was not a time picker at all: it was the generic list dropdown, handed
 * ninety-six options and a search box. Picking half past two meant opening a panel that showed
 * about twelve of them, then scrolling or typing "2" and reading past every other two in the
 * day. There was no keyboard path through it worth the name — ninety-six buttons in tab order —
 * and neither of the two fields had a label a screen reader could reach.
 *
 * So: three segments, each individually focusable, each stepped with the arrow keys, and typing
 * a digit overwrites and moves on. That is the macOS time field, and it is the shape people
 * already know. The chevron still opens a list for whoever would rather point at a time than
 * type one — but a short one, snapped to the step and scrolled to where you already are.
 *
 * The value stays 'HH:MM', twenty-four hour, in and out. That is what the server stores and
 * what lib/dates.js speaks; twelve-hour with a meridiem is a display choice this file makes and
 * does not leak. An empty string means no time set, which is a real state — "part of the day"
 * opens with both ends deliberately blank.
 */

const parse = (hhmm) => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return { h: null, m: null, mer: null }
  const [H, m] = hhmm.split(':').map(Number)
  return { h: H % 12 === 0 ? 12 : H % 12, m, mer: H < 12 ? 'AM' : 'PM' }
}

const to24 = ({ h, m, mer }) => {
  const H = mer === 'AM' ? (h === 12 ? 0 : h) : h === 12 ? 12 : h + 12
  return `${String(H).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const complete = (p) => p.h != null && p.m != null && Boolean(p.mer)
const empty = (p) => p.h == null && p.m == null && !p.mer

const wrap = (n, lo, hi) => (n < lo ? hi : n > hi ? lo : n)

export default function TimeField({
  value = '',
  onChange,
  step = 15,
  min,
  label,
  id,
  disabled = false,
}) {
  const [parts, setParts] = useState(() => parse(value))
  const [seg, setSeg] = useState(0)
  const [open, setOpen] = useState(false)
  // Digits typed so far in the segment being edited. Cleared whenever focus moves, so "1"
  // then a pause then "2" is twelve o'clock and "1" then Tab then "2" is not.
  const buf = useRef('')
  const segRefs = useRef([])
  const anchorRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => { setParts(parse(value)) }, [value])

  const focusSeg = (i) => {
    buf.current = ''
    setSeg(i)
    segRefs.current[i]?.focus()
  }

  /** Update, and tell the caller only once there is a whole time to tell them about. */
  const commit = (next) => {
    setParts(next)
    if (complete(next)) onChange(to24(next))
    else if (empty(next)) onChange('')
  }

  /**
   * Stepping starts somewhere sensible when the field is blank.
   *
   * Arrowing up on an empty field should give you a time, not nothing. Nine in the morning is
   * the least surprising one in a scheduling app; the other two segments fill in with it so a
   * single keypress leaves a usable value rather than a third of one.
   */
  const base = () => (complete(parts) ? parts : { h: 9, m: 0, mer: 'AM' })

  const bump = (dir) => {
    const p = complete(parts) ? { ...parts } : base()
    if (seg === 0) p.h = wrap(p.h + dir, 1, 12)
    else if (seg === 1) {
      // Steps by the same increment the list offers, and wraps inside the hour rather than
      // carrying into it — the macOS behaviour, and the one that makes an hour easy to hold
      // still while you change the minutes.
      const stepped = Math.round(p.m / step) * step + dir * step
      p.m = stepped < 0 ? 60 - step : stepped >= 60 ? 0 : stepped
    } else p.mer = p.mer === 'AM' ? 'PM' : 'AM'
    commit(p)
  }

  const typeDigit = (d) => {
    const p = complete(parts) ? { ...parts } : { ...base(), ...(empty(parts) ? { m: 0 } : {}) }
    const next = buf.current + d
    if (seg === 0) {
      const n = Number(next)
      if (next.length === 1) {
        // A leading 1 could still become 10, 11 or 12, so it waits; 2 through 9 cannot, and
        // moving on immediately is what makes typing a time feel quick rather than deliberate.
        p.h = d === '0' ? 1 : Number(d)
        commit(p)
        if (d === '1') { buf.current = next; return }
        buf.current = ''
        focusSeg(1)
        return
      }
      p.h = n >= 1 && n <= 12 ? n : Number(d)
      commit(p)
      buf.current = ''
      focusSeg(1)
      return
    }
    if (seg === 1) {
      if (next.length === 1) {
        p.m = Number(d)
        commit(p)
        if (Number(d) <= 5) { buf.current = next; return }
        buf.current = ''
        focusSeg(2)
        return
      }
      p.m = Math.min(59, Number(next))
      commit(p)
      buf.current = ''
      focusSeg(2)
    }
  }

  const onKeyDown = (e) => {
    const k = e.key
    if (k === 'ArrowUp' || k === 'ArrowDown') { e.preventDefault(); bump(k === 'ArrowUp' ? 1 : -1); return }
    if (k === 'ArrowLeft') { e.preventDefault(); focusSeg(Math.max(0, seg - 1)); return }
    if (k === 'ArrowRight') { e.preventDefault(); focusSeg(Math.min(2, seg + 1)); return }
    if (k === 'Home') { e.preventDefault(); focusSeg(0); return }
    if (k === 'End') { e.preventDefault(); focusSeg(2); return }
    if (k === 'Backspace' || k === 'Delete') {
      e.preventDefault()
      buf.current = ''
      commit({ h: null, m: null, mer: null })
      return
    }
    if (seg === 2 && (k.toLowerCase() === 'a' || k.toLowerCase() === 'p')) {
      e.preventDefault()
      commit({ ...(complete(parts) ? parts : base()), mer: k.toLowerCase() === 'a' ? 'AM' : 'PM' })
      return
    }
    if (/^\d$/.test(k) && seg < 2) { e.preventDefault(); typeDigit(k) }
  }

  /** The list: every `step` of the day, which at fifteen minutes is a quarter of the old one. */
  const options = useMemo(
    () => Array.from({ length: Math.floor((24 * 60) / step) }, (_, i) => {
      const mins = i * step
      const hhmm = `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
      return { value: hhmm, label: formatTime(hhmm) }
    }),
    [step],
  )

  const shown = complete(parts) ? parts : null
  // The hour is never zero-padded and the minute always is, which is how formatTime writes a
  // time everywhere else in the app — '1:45am', not '01:45am'.
  const hourText = shown ? String(shown.h) : '--'
  const minuteText = shown ? String(shown.m).padStart(2, '0') : '--'

  const segProps = (i, name, valueNow, valueText, textMin, textMax) => ({
    ref: (el) => { segRefs.current[i] = el },
    role: 'spinbutton',
    tabIndex: disabled ? -1 : seg === i ? 0 : -1,
    'aria-label': name,
    'aria-valuenow': valueNow ?? undefined,
    'aria-valuetext': valueText,
    'aria-valuemin': textMin,
    'aria-valuemax': textMax,
    className: `tf-seg ${seg === i ? 'on' : ''}`,
    onFocus: () => setSeg(i),
    onMouseDown: (e) => { e.preventDefault(); focusSeg(i) },
  })

  return (
    <div className={`tf ${disabled ? 'disabled' : ''}`} ref={anchorRef}>
      <div
        className="tf-field"
        role="group"
        id={id}
        aria-label={label}
        aria-disabled={disabled || undefined}
        onKeyDown={disabled ? undefined : onKeyDown}
      >
        <Icon name="clock" size={13} />
        <span className="tf-segs">
          <span {...segProps(0, 'Hour', shown?.h, shown ? String(shown.h) : 'empty', 1, 12)}>
            {hourText}
          </span>
          <span className="tf-colon" aria-hidden="true">:</span>
          <span {...segProps(1, 'Minute', shown?.m, shown ? String(shown.m).padStart(2, '0') : 'empty', 0, 59)}>
            {minuteText}
          </span>
          <span {...segProps(2, 'AM or PM', undefined, shown?.mer || 'empty')}>
            {shown?.mer || '--'}
          </span>
        </span>
        <button
          type="button"
          className="tf-open"
          disabled={disabled}
          aria-label={label ? `${label}: choose from a list` : 'Choose a time from a list'}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="down" size={12} strokeWidth={2.2} />
        </button>
      </div>

      <Popover
        open={open}
        anchorRef={anchorRef}
        close={close}
        className="tf-list"
        role="listbox"
        label={label || 'Times'}
        matchWidth
      >
        <TimeList
          options={options}
          value={value}
          min={min}
          onPick={(v) => { close(); onChange(v); focusSeg(0) }}
        />
      </Popover>
    </div>
  )
}

/**
 * The list, opened on the time already chosen rather than at midnight.
 *
 * A list of a whole day that always starts at 00:00 makes the common case — nudging an
 * afternoon time by a quarter of an hour — a scroll every single time.
 */
function TimeList({ options, value, min, onPick }) {
  const ref = useRef(null)
  const usable = options.filter((o) => !min || o.value >= min)
  const current = usable.findIndex((o) => o.value === value)
  const [active, setActive] = useState(current >= 0 ? current : 0)

  useEffect(() => {
    const el = ref.current?.querySelector('[data-active="true"]')
    el?.scrollIntoView({ block: 'center' })
    ref.current?.focus()
  }, [])

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(usable.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0) }
    else if (e.key === 'End') { e.preventDefault(); setActive(usable.length - 1) }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(usable[active]?.value) }
  }

  useEffect(() => {
    ref.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  return (
    <div className="tf-list-scroll" ref={ref} tabIndex={-1} onKeyDown={onKeyDown}>
      {usable.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="option"
          aria-selected={o.value === value}
          data-i={i}
          data-active={i === active ? 'true' : undefined}
          className={`tf-option ${o.value === value ? 'on' : ''} ${i === active ? 'active' : ''}`}
          onMouseEnter={() => setActive(i)}
          onClick={() => onPick(o.value)}
        >
          {o.label}
        </button>
      ))}
      {usable.length === 0 && <div className="ctx-label">No times available</div>}
    </div>
  )
}
